// Motor genérico de plantillas .docx (cliente).
// Descarga una plantilla Word desde /public/assets/plantillas, reemplaza sus
// marcadores `{CLAVE}` con datos y descarga el documento resultante. Es la base
// modular para TODOS los documentos del sistema (acta de reparto, autos, oficios…):
// para añadir un documento nuevo basta con (1) subir su plantilla a assets,
// (2) registrarla en `registro.ts` y (3) escribir un armador de datos.
//
// Usa docxtemplater (no reemplazo de texto crudo) porque Word suele partir un
// marcador en varios "runs" XML —lo que rompe un simple String.replace— y porque
// soporta bucles `{#lista}…{/lista}` para tablas de filas variables.
// Las librerías se cargan de forma diferida (dynamic import) para no incluirlas
// en el bundle inicial ni evaluarlas en SSR (solo corren al generar el documento).

export const MIME_DOCX =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** La plantilla no está en /public/assets/plantillas (aún no la han cargado). */
export class PlantillaFaltanteError extends Error {
  constructor(public readonly ruta: string, mensaje: string) {
    super(mensaje);
    this.name = 'PlantillaFaltanteError';
  }
}

/** Descarga la plantilla desde /public/assets o lanza PlantillaFaltanteError. */
export async function cargarPlantilla(rutaPlantilla: string): Promise<ArrayBuffer> {
  try {
    const res = await fetch(rutaPlantilla);
    if (!res.ok) {
      throw new PlantillaFaltanteError(
        rutaPlantilla,
        `No se encontró la plantilla en ${rutaPlantilla} (HTTP ${res.status}).`,
      );
    }
    return await res.arrayBuffer();
  } catch (e) {
    if (e instanceof PlantillaFaltanteError) throw e;
    throw new PlantillaFaltanteError(
      rutaPlantilla,
      `No se pudo cargar la plantilla ${rutaPlantilla}: ${(e as Error).message}`,
    );
  }
}

/**
 * Rellena `rutaPlantilla` con `datos` y devuelve el .docx como Blob (sin descargar).
 * Útil para previsualizar (docx-preview) y descargar desde el mismo resultado.
 * @throws PlantillaFaltanteError si la plantilla no existe (HTTP no OK).
 */
export async function renderDocxBlob(
  rutaPlantilla: string,
  datos: Record<string, unknown>,
  opciones?: { marcaBorrador?: boolean },
): Promise<Blob> {
  const contenido = await cargarPlantilla(rutaPlantilla);
  const [{ default: PizZip }, { default: Docxtemplater }, { marcarBorradorZip }] = await Promise.all([
    import('pizzip'),
    import('docxtemplater'),
    import('./marca-agua'),
  ]);

  const zip = new PizZip(contenido);
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true, // permite `{#lista}` que abarque párrafos/filas
    linebreaks: true, // respeta saltos de línea en los valores
    delimiters: { start: '{', end: '}' }, // marcadores estilo {CLAVE}
    nullGetter: () => '', // marcador sin dato → vacío (no "undefined")
  });

  doc.render(datos);
  const resultado = doc.getZip();
  // Marca de agua BORRADOR (documento aún sin la cadena de firmas completa).
  if (opciones?.marcaBorrador) marcarBorradorZip(resultado);
  return resultado.generate({ type: 'blob', mimeType: MIME_DOCX });
}

/**
 * Rellena `rutaPlantilla` con `datos` y dispara la descarga como `nombreArchivo`.
 * @throws PlantillaFaltanteError si la plantilla no existe (HTTP no OK).
 */
export async function generarDocx(
  rutaPlantilla: string,
  datos: Record<string, unknown>,
  nombreArchivo: string,
  opciones?: { marcaBorrador?: boolean },
): Promise<void> {
  const blob = await renderDocxBlob(rutaPlantilla, datos, opciones);
  const { saveAs } = await import('file-saver');
  saveAs(blob, nombreArchivo);
}
